// ╔══════════════════════════════════════════════════════════════╗
// ║  Payslip Detail — HTML-based, printable                       ║
// ║  Gradient header + employee block + earnings/deductions       ║
// ║  + net pay band. (No bank / CR / tax / QR / signatures.)      ║
// ╚══════════════════════════════════════════════════════════════╝

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtAR } from '../lib/format.js';
import { Btn } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';

export default function PayslipDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['payslip', id],
    queryFn: () => api.get(`/payroll/payslip/${id}`).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="p-6 grid place-items-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-4 border-brand-200 border-t-brand-500 animate-spin" />
      </div>
    );
  }
  if (error || !data?.payslip) {
    return (
      <div className="p-8 text-center">
        <Icon name="alert" size={48} className="mx-auto text-rose-400 mb-3" />
        <h2 className="font-bold text-lg">لم يتم العثور على كشف الراتب</h2>
        <Btn variant="outline" className="mt-4" onClick={() => navigate(-1)}>رجوع</Btn>
      </div>
    );
  }

  const s = data.payslip;
  const emp = s.employee;
  const run = s.payrollRun;

  const earnings = [
    ['الراتب الأساسي', Number(s.baseSalary)],
    ['البدلات', Number(s.allowance)],
    ['ساعات إضافية', Number(s.overtimeAmount)],
    ['مكافأة إنتاج', Number(s.productionBonus)],
    ['مكافآت أخرى', Number(s.otherBonuses)],
  ].filter(([, v]) => v > 0);

  const deductions = [
    ['التأمينات الاجتماعية', Number(s.insurance)],
    ['ضريبة كسب العمل', Number(s.tax)],
    ['خصم غياب', Number(s.absenceDeduction)],
    ['خصم تأخير', Number(s.lateDeduction)],
    ['قسط سلفة', Number(s.advanceDeduction)],
    ['خصومات أخرى', Number(s.otherDeductions)],
  ].filter(([, v]) => v > 0);

  const totalE = earnings.reduce((a, b) => a + b[1], 0);
  const totalD = deductions.reduce((a, b) => a + b[1], 0);
  const net = Number(s.net);

  const monthIso = run?.month ?? '';
  const monthLabel = monthIso
    ? new Date(monthIso).toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' })
    : '';
  const payslipNo = `PS-${emp?.id ?? ''}-${monthIso.slice(0, 7)}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 fade-up">
      {/* Action bar — hidden in print */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 no-print">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            كشف الراتب
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">معاينة كشف الراتب</h1>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" icon="arrowR" onClick={() => navigate(-1)}>رجوع</Btn>
          <Btn variant="primary" icon="file" onClick={() => window.print()}>طباعة</Btn>
        </div>
      </div>

      {/* The printable card */}
      <div className="bg-white text-ink-800 max-w-4xl mx-auto rounded-2xl ring-1 ring-ink-200 shadow-pop overflow-hidden print:shadow-none print:ring-0">
        {/* Gradient header */}
        <div className="relative p-6 sm:p-8 bg-gradient-to-bl from-brand-950 via-brand-900 to-brand-700 text-brand-50">
          <div className="absolute inset-0 dot-grid opacity-20" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img
                src="/logo.jpg"
                alt=""
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl ring-2 ring-brand-300/50 object-cover"
              />
              <div>
                <div className="text-[10px] sm:text-[11px] tracking-[0.2em] text-brand-200 font-mono">
                  REDA HINDAM GARMENT MFG.
                </div>
                <h2 className="text-lg sm:text-2xl font-extrabold mt-0.5">
                  مصانع رداء هندام للملابس الجاهزة
                </h2>
                <p className="text-[10px] sm:text-xs text-brand-100/80 mt-1">
                  أبو كبير · الشرقية
                </p>
              </div>
            </div>
            <div className="text-end shrink-0">
              <div className="text-[10px] text-brand-200 font-mono">PAYSLIP / كشف راتب</div>
              <div className="text-2xl sm:text-3xl font-black tracking-tight mt-1">
                {monthLabel}
              </div>
              <div className="text-[10px] text-brand-100/80 font-mono mt-1">{payslipNo}</div>
            </div>
          </div>
        </div>

        {/* Employee block */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 sm:p-6 border-b border-ink-100 bg-ink-50">
          {[
            ['الموظف', emp?.name],
            ['الرقم الوظيفي', emp?.id],
            ['القسم', emp?.department?.nameAr ?? emp?.deptId],
            ['المسمى الوظيفي', emp?.position],
            ['تاريخ التعيين', emp?.hiredOn],
            ['الوردية', emp?.shift?.nameAr ?? emp?.shiftId],
            ['الرقم القومي', emp?.nid ?? '—'],
          ].map(([k, v], i) => (
            <div key={i}>
              <div className="text-[10px] text-ink-400 uppercase tracking-wider">{k}</div>
              <div className="text-sm font-semibold mt-0.5">{v ?? '—'}</div>
            </div>
          ))}
        </div>

        {/* Earnings & deductions */}
        <div className="grid md:grid-cols-2 md:divide-x divide-ink-100">
          <div className="p-5 sm:p-6">
            <h3 className="text-xs font-bold text-emerald-600 tracking-wider uppercase mb-3">
              المستحقّات · Earnings
            </h3>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-ink-100">
                {earnings.map(([k, v]) => (
                  <tr key={k}>
                    <td className="py-2 text-ink-500">{k}</td>
                    <td className="py-2 text-end tabular font-semibold">{fmtAR(v)}</td>
                  </tr>
                ))}
                <tr className="bg-emerald-50">
                  <td className="py-2 font-bold">إجمالي المستحقّات</td>
                  <td className="py-2 text-end tabular font-extrabold">{fmtAR(totalE)} EGP</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="p-5 sm:p-6 border-t md:border-t-0 border-ink-100">
            <h3 className="text-xs font-bold text-rose-600 tracking-wider uppercase mb-3">
              الاستقطاعات · Deductions
            </h3>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-ink-100">
                {deductions.map(([k, v]) => (
                  <tr key={k}>
                    <td className="py-2 text-ink-500">{k}</td>
                    <td className="py-2 text-end tabular font-semibold">-{fmtAR(v)}</td>
                  </tr>
                ))}
                <tr className="bg-rose-50">
                  <td className="py-2 font-bold">إجمالي الاستقطاعات</td>
                  <td className="py-2 text-end tabular font-extrabold">-{fmtAR(totalD)} EGP</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Net pay band */}
        <div className="p-5 sm:p-6 bg-gradient-to-l from-brand-100 via-brand-50 to-white border-y border-brand-200/60">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-brand-700 font-bold">
                صافي الراتب · NET PAY
              </div>
              <div className="text-3xl sm:text-5xl font-black text-brand-800 tabular mt-1">
                {fmtAR(net)} <span className="text-sm sm:text-base font-bold">EGP</span>
              </div>
            </div>
            <div className="text-end">
              <div className="text-[10px] text-ink-400 uppercase tracking-wider">تاريخ الصرف</div>
              <div className="text-base font-bold tabular mt-1">
                {monthIso ? monthIso.slice(0, 7) + '-31' : '—'}
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-4 text-[10px] text-ink-400 text-center bg-ink-50 border-t border-ink-100">
          هذا المستند صادر إلكترونياً. تم الإصدار في{' '}
          {new Date().toLocaleDateString('ar-EG')} عبر رداء هندام HRMS
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .dot-grid { background: none !important; }
        }
      `}</style>
    </div>
  );
}
